/* línkè · 聊天代理调用 ===========================================
   走后端 /api/chat 路由（Ombre Brain server.py 的 chat_proxy）
   后端再代理到 Claude API（官方 or gemai 中转，由 .env 控制）
   Cookie 由浏览器自动带（与 vite proxy 同源，credentials: include）
   ================================================================ */

/**
 * 发起一次聊天请求
 * @param {Array} messages  [{role:'user'|'assistant', content:'...'}]
 * @param {Object} options
 *   - system    string  系统提示
 *   - model     string  覆盖默认模型
 *   - max_tokens number 覆盖默认 4096
 *   - signal    AbortSignal  用于中断
 * @returns {Promise<{ok,message,usage,model,stop_reason}>}
 */
export async function sendChat(messages, options = {}) {
  const body = { messages };
  if (options.system) body.system = options.system;
  if (options.model) body.model = options.model;
  if (options.max_tokens) body.max_tokens = options.max_tokens;
  // temperature 0 是合法值，要显式 typeof 判断
  if (typeof options.temperature === 'number') body.temperature = options.temperature;
  // provider 覆盖：{ api_key?, base_url?, model? }
  // 后端 chat_proxy 收到后会覆盖 .env 的默认 Provider
  if (options.provider && typeof options.provider === 'object') {
    const p = {};
    if (options.provider.api_key) p.api_key = options.provider.api_key;
    if (options.provider.base_url) p.base_url = options.provider.base_url;
    if (options.provider.model) p.model = options.provider.model;
    if (Object.keys(p).length > 0) body.provider = p;
  }

  const res = await fetch('/api/chat', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
    signal: options.signal,
  });

  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch (e) {
    throw new Error('后端返回非 JSON：' + text.slice(0, 120));
  }

  if (!res.ok) {
    // 优先用后端给的错误描述，否则用 HTTP 状态
    const msg = data.error || `HTTP ${res.status}`;
    const detail = data.detail ? `（${String(data.detail).slice(0, 120)}）` : '';
    const err = new Error(msg + detail);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

/** 提取 AI 消息文本（已经是字符串，向后兼容写法）。 */
export function extractAssistantText(reply) {
  if (!reply) return '';
  if (typeof reply.message?.content === 'string') return reply.message.content;
  // 万一后端某天改成 content blocks 数组
  if (Array.isArray(reply.message?.content)) {
    return reply.message.content
      .filter((b) => b?.type === 'text')
      .map((b) => b.text || '')
      .join('\n');
  }
  return '';
}

/* ============================================================
   流式聊天（SSE + Tool Loop）—— 2026-05-26 加入
   后端 /api/chat 收到 stream:true 走 SSE，吐 4 种事件：
     - tool_use     {name, input, id}     克克在用某工具
     - tool_result  {name, id, ok, summary}  工具执行结果摘要
     - text         {text}                最终 assistant 文本
     - error        {message, detail}     上游/工具错误
     - done         {usage, model, stop_reason}  整轮结束
   ============================================================ */

/**
 * 发起一次"克克带工具"的流式聊天。
 * @param {Array} messages
 * @param {Object} options 同 sendChat（system/model/max_tokens/temperature/provider/signal）
 * @param {Function} onEvent  (eventName, payload) => void，每个 SSE 事件回调一次
 * @returns {Promise<{text:string, usage:object, model:string, stop_reason:string}>}
 *   resolve 时刻 = 收到 done 事件；如果收到 error 事件会 reject
 */
export async function streamChat(messages, options = {}, onEvent = () => {}) {
  const body = { messages, stream: true };
  if (options.conversation_id) body.conversation_id = options.conversation_id;
  // 深度模式：开关打开时塞 engine:"claude-code"，后端改用常驻的 Claude Code 本体
  // （走 Pro 订阅，不走 API）。CC 模式下 system/model/provider 等字段后端会忽略，继续发无害。
  if (options.engine) body.engine = options.engine;
  // ③ 手机推送：开了「手机推送」开关时带 notify，后端在回复收完且你不在看时推 Bark
  if (options.notify) body.notify = true;
  // 深度模式时间注入：铃的本地时间字符串，后端注进 cc-web prompt
  if (options.client_now) body.client_now = options.client_now;
  // 「开新一段」：她点「新对话」后的第一条消息带 reset_context，让深度模式后端先给克克
  // 开一个全新干净会话（只对 engine:claude-code 有意义，中转模式后端会无害忽略）。
  if (options.reset_context) body.reset_context = true;
  // 「和爸爸一起听」：带上这首歌 {id,name,artist}，深度模式后端会取它真正的歌词注给克克，
  // 让他在对话里就着真词分享这首（中转模式后端无害忽略）。
  if (options.listen_song) body.listen_song = options.listen_song;
  if (options.system) body.system = options.system;
  if (options.model) body.model = options.model;
  if (options.max_tokens) body.max_tokens = options.max_tokens;
  if (typeof options.temperature === 'number') body.temperature = options.temperature;
  if (options.provider && typeof options.provider === 'object') {
    const p = {};
    if (options.provider.api_key) p.api_key = options.provider.api_key;
    if (options.provider.base_url) p.base_url = options.provider.base_url;
    if (options.provider.model) p.model = options.provider.model;
    if (Object.keys(p).length > 0) body.provider = p;
  }

  const res = await fetch('/api/chat', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(body),
    signal: options.signal,
  });

  if (!res.ok) {
    // 服务器在 status_code != 200 时仍可能返回 JSON 错误（如 401）
    let errMsg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j.error) errMsg = j.error;
    } catch {}
    const err = new Error(errMsg);
    err.status = res.status;
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let finalText = '';
  let finalMeta = {};

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE 帧分隔符是 \n\n
    let sep;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      if (!frame.trim()) continue;

      // 解析帧：event: name\ndata: {...json...}
      let eventName = 'message';
      let dataLine = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) eventName = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLine += line.slice(5).trim();
      }
      let payload = {};
      try {
        if (dataLine) payload = JSON.parse(dataLine);
      } catch {
        payload = { _raw: dataLine };
      }

      if (eventName === 'error') {
        const e = new Error(payload.message || '后端报错');
        e.detail = payload.detail;
        throw e;
      }
      if (eventName === 'text') finalText = payload.text || '';
      if (eventName === 'done') finalMeta = payload;
      onEvent(eventName, payload);
    }
  }

  return {
    text: finalText,
    usage: finalMeta.usage || {},
    model: finalMeta.model,
    stop_reason: finalMeta.stop_reason,
  };
}
